import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, ExternalLink, Hash, Lock, Loader2, RefreshCw, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuthApi';
import { toast } from '@/hooks/use-toast';
import { queryKeys } from '@/queries/keys';
import slackService, { SlackChannel, SlackConfig, UpdateSlackNotifications } from '@/lib/api/slack';

interface SlackConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

const NOTIFICATION_TOGGLES: {
  key: keyof UpdateSlackNotifications;
  label: string;
  description: string;
}[] = [
  { key: 'notify_on_qualified', label: 'Qualified leads', description: 'When a visitor is identified as a qualified lead' },
  { key: 'notify_on_booked', label: 'Meetings booked', description: 'When a visitor books a meeting' },
  { key: 'notify_on_active_session', label: 'Active sessions', description: 'When a visitor starts interacting with your agent' },
  { key: 'notify_on_high_intent', label: 'High intent signals', description: 'When a visitor shows strong buying intent' },
  { key: 'notify_on_all_calls', label: 'All calls', description: 'Notify on every conversation (can be noisy)' },
];

const SlackConfigDialog: React.FC<SlackConfigDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [channelPopoverOpen, setChannelPopoverOpen] = useState(false);
  const channelDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!channelPopoverOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target as Node)) {
        setChannelPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [channelPopoverOpen]);

  const configQuery = useQuery({
    queryKey: queryKeys.slack.config(organizationId),
    enabled: open && !!organizationId,
    queryFn: async () => {
      const response = await slackService.getConfig(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load Slack configuration.');
      }
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateSlackNotifications) => {
      const response = await slackService.updateNotifications(organizationId, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update notification settings.');
      }
      return response.data;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.slack.config(organizationId) });
      const previous = queryClient.getQueryData<SlackConfig>(queryKeys.slack.config(organizationId));
      if (previous) {
        queryClient.setQueryData<SlackConfig>(queryKeys.slack.config(organizationId), {
          ...previous,
          ...data,
        });
      }
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.slack.config(organizationId), context.previous);
      }
      toast({
        title: 'Update failed',
        description: 'Could not save notification settings. Please try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.slack.config(organizationId) });
    },
  });

  const config = configQuery.data;

  const channelsQuery = useQuery({
    queryKey: queryKeys.slack.channels(organizationId),
    enabled: open && !!organizationId && !!config?.connected,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await slackService.listChannels(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load channels.');
      }
      return response.data.channels;
    },
  });

  const channelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const response = await slackService.updateNotifications(organizationId, {
        notification_channel_id: channelId,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update notification channel.');
      }
      return response.data;
    },
    onMutate: async (channelId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.slack.config(organizationId) });
      const previous = queryClient.getQueryData<SlackConfig>(queryKeys.slack.config(organizationId));
      if (previous) {
        const selectedChannel = channelsQuery.data?.find((ch: SlackChannel) => ch.id === channelId);
        queryClient.setQueryData<SlackConfig>(queryKeys.slack.config(organizationId), {
          ...previous,
          notification_channel_id: channelId,
          notification_channel_name: selectedChannel?.name ?? previous.notification_channel_name,
        });
      }
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.slack.config(organizationId), context.previous);
      }
      toast({
        title: 'Update failed',
        description: 'Could not update notification channel. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({ title: 'Channel updated', description: 'Notification channel has been updated.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.slack.config(organizationId) });
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const response = await slackService.testNotification(organizationId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to send test notification.');
      }
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Test sent', description: 'Check your Slack channel for the test notification.' });
    },
    onError: () => {
      toast({
        title: 'Test failed',
        description: 'Could not send test notification. Make sure a channel is selected.',
        variant: 'destructive',
      });
    },
  });

  const handleToggle = (key: keyof UpdateSlackNotifications, currentValue: boolean) => {
    updateMutation.mutate({ [key]: !currentValue });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Slack Notifications</DialogTitle>
          <DialogDescription>
            Choose which events send notifications to your Slack channel.
          </DialogDescription>
        </DialogHeader>

        {configQuery.isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading configuration...
          </div>
        ) : configQuery.isError ? (
          <div className="py-8 text-center text-sm text-destructive">
            Failed to load Slack configuration.
          </div>
        ) : !config?.connected ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">Slack is not connected yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Connect your Slack workspace to receive notifications about leads and engagement.</p>
            <Button
              className="mt-4 gap-2"
              onClick={() => {
                window.location.href = slackService.getInstallUrl(organizationId, user?.id);
              }}
            >
              <ExternalLink className="h-4 w-4" /> Connect to Slack
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {config.slack_team_name && (
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                <span>Team: <span className="font-medium text-foreground">{config.slack_team_name}</span></span>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Notification channel</Label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  disabled={channelsQuery.isFetching}
                  onClick={() => channelsQuery.refetch()}
                  title="Refresh channels"
                >
                  <RefreshCw className={cn('h-3 w-3', channelsQuery.isFetching && 'animate-spin')} />
                </Button>
              </div>
              <div ref={channelDropdownRef}>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={channelPopoverOpen}
                className="w-full justify-between font-normal"
                disabled={channelMutation.isPending}
                onClick={() => setChannelPopoverOpen(!channelPopoverOpen)}
              >
                {channelsQuery.isLoading ? (
                  <span className="text-muted-foreground">Loading channels...</span>
                ) : config.notification_channel_id ? (
                  <span className="flex items-center gap-1.5 truncate">
                    {(() => {
                      const selected = channelsQuery.data?.find(
                        (ch: SlackChannel) => ch.id === config.notification_channel_id
                      );
                      return selected ? (
                        <>
                          {selected.is_private ? (
                            <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                          ) : (
                            <Hash className="h-3 w-3 shrink-0 text-muted-foreground" />
                          )}
                          {selected.name}
                        </>
                      ) : (
                        config.notification_channel_name || 'Select a channel'
                      );
                    })()}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select a channel</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {channelPopoverOpen && (
                <div className="rounded-md border bg-popover shadow-md">
                  <Command>
                    <CommandInput placeholder="Search channels..." />
                    <CommandList className="max-h-[200px]">
                      {channelsQuery.isLoading ? (
                        <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Loading...
                        </div>
                      ) : channelsQuery.isError ? (
                        <div className="py-4 text-center text-sm text-destructive">
                          Failed to load channels
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No channels found.</CommandEmpty>
                          <CommandGroup>
                            {channelsQuery.data?.map((channel: SlackChannel) => (
                              <CommandItem
                                key={channel.id}
                                value={channel.name}
                                onSelect={() => {
                                  channelMutation.mutate(channel.id);
                                  setChannelPopoverOpen(false);
                                }}
                              >
                                <span className="flex items-center gap-1.5 flex-1">
                                  {channel.is_private ? (
                                    <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                                  ) : (
                                    <Hash className="h-3 w-3 shrink-0 text-muted-foreground" />
                                  )}
                                  {channel.name}
                                </span>
                                <Check
                                  className={cn(
                                    'ml-auto h-4 w-4',
                                    config.notification_channel_id === channel.id
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}
              </div>
              <p className="text-xs text-muted-foreground">
                Where notifications will be posted
              </p>
            </div>

            <div className="space-y-3">
              {NOTIFICATION_TOGGLES.map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={`slack-${key}`} className="text-sm font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={`slack-${key}`}
                    checked={!!config[key as keyof SlackConfig]}
                    onCheckedChange={() => handleToggle(key, !!config[key as keyof SlackConfig])}
                  />
                </div>
              ))}
            </div>

          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {config?.connected && (
            <>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDisconnectConfirm(true)}
            >
              Disconnect
            </Button>
            <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect Slack?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will stop receiving notifications. You can reconnect at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async () => {
                      const response = await slackService.disconnect(organizationId);
                      if (response.success) {
                        queryClient.invalidateQueries({ queryKey: queryKeys.slack.config(organizationId) });
                        toast({ title: 'Slack disconnected', description: 'Your Slack workspace has been disconnected.' });
                        onOpenChange(false);
                      } else {
                        toast({ title: 'Failed to disconnect', description: 'Please try again.', variant: 'destructive' });
                      }
                    }}
                  >
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            </>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {config?.connected && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={!config.notification_channel_id || testMutation.isPending}
                onClick={() => testMutation.mutate()}
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Test
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SlackConfigDialog;
